import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import DataTable from '../common/DataTable';
import CompactFilterPanel from '../common/CompactFilterPanel';
import { FilterCriteria } from '../../types/company';
import { ApiService } from '../../services/api';
import { getErrorSeverity } from '../../utils/errorUtils';

interface Log {
  id: number;
  user_id?: number | null;
  username?: string | null;
  table_name: string;
  operation: string;
  record_id: number;
  name?: string | null;
  old_values?: string | null;
  new_values?: string | null;
  changed_fields?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  timestamp: string;
}

interface LogResponse {
  data: Log[];
  pagination: {
    total: number;
    hasMore: boolean;
    nextCursor: number | null;
    limit: number;
  };
}

interface LogTableColumn {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any) => string;
  type?: 'boolean' | 'text' | 'number';
  visible: boolean;
}

interface SortConfig {
  column: string;
  direction: 'ASC' | 'DESC';
}

const LogsView: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
    nextCursor: null as number | null,
    limit: 50
  });
  const [appliedFilters, setAppliedFilters] = useState<FilterCriteria[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'id',
    direction: 'DESC'
  });
  const scrollPositionRef = useRef<number>(0);

  // Format JSON values for display - defined before columns so it can be used in column format
  const formatJsonValue = (value: string | object | null | undefined): string => {
    if (!value) return '';
    try {
      // If value is already an object, stringify it directly
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      // If value is a string, try to parse it first, then stringify
      if (typeof value === 'string') {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      }
      return String(value);
    } catch {
      // If parsing fails, return as string
      return typeof value === 'string' ? value : String(value);
    }
  };

  // Format timestamp with timezone
  const formatTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Format: "MM/DD/YYYY, HH:MM:SS AM/PM (Timezone)"
      return `${date.toLocaleString()} (${timezone})`;
    } catch {
      return timestamp || '';
    }
  };

  const allColumns: LogTableColumn[] = [
    { id: 'id', label: 'ID', minWidth: 80, type: 'number', visible: true },
    { id: 'user_id', label: 'User ID', minWidth: 100, type: 'number', visible: true },
    { id: 'username', label: 'Username', minWidth: 150, type: 'text', visible: true },
    { id: 'table_name', label: 'Table', minWidth: 150, type: 'text', visible: true },
    { id: 'operation', label: 'Operation', minWidth: 120, type: 'text', visible: true },
    { id: 'record_id', label: 'Record ID', minWidth: 100, type: 'number', visible: true },
    { id: 'name', label: 'Name', minWidth: 200, type: 'text', visible: true },
    { id: 'old_values', label: 'Old Values', minWidth: 200, type: 'text', visible: true, format: (value: any) => formatJsonValue(value) },
    { id: 'new_values', label: 'New Values', minWidth: 200, type: 'text', visible: true, format: (value: any) => formatJsonValue(value) },
    { id: 'changed_fields', label: 'Changed Fields', minWidth: 150, type: 'text', visible: true, format: (value: any) => formatJsonValue(value) },
    { id: 'ip_address', label: 'IP Address', minWidth: 150, type: 'text', visible: false },
    { id: 'user_agent', label: 'User Agent', minWidth: 200, type: 'text', visible: false },
    { id: 'timestamp', label: 'Timestamp', minWidth: 180, type: 'text', visible: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => ({ id: col.id, label: col.label, visible: col.visible }))
  );

  const columns = allColumns.filter(col => 
    visibleColumns.find(vc => vc.id === col.id)?.visible
  );

  const loadLogs = useCallback(async (
    cursor: number | null, 
    isNextPage: boolean,
    filters: FilterCriteria[]
  ) => {
    if (isNextPage) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      let response: LogResponse;
      
      if (filters.length > 0) {
        response = await ApiService.searchLogs({
          filters,
          cursor,
          limit: pagination.limit,
          sort: sortConfig.column,
          direction: sortConfig.direction
        });
      } else {
        response = await ApiService.getLogs(
          cursor,
          pagination.limit,
          sortConfig.column,
          sortConfig.direction
        );
      }

      if (isNextPage) {
        setLogs(prev => [...prev, ...response.data]);
        
        setTimeout(() => {
          if (scrollPositionRef.current > 0) {
            window.scrollTo(0, scrollPositionRef.current);
          }
        }, 0);
      } else {
        setLogs(response.data);
        scrollPositionRef.current = 0;
      }

      setPagination({
        total: response.pagination.total,
        hasMore: response.pagination.hasMore,
        nextCursor: response.pagination.nextCursor,
        limit: response.pagination.limit
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load logs');
      console.error('Error loading logs:', err);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [pagination.limit, sortConfig]);

  // Initial load
  useEffect(() => {
    loadLogs(null, false, appliedFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const handleLoadMore = () => {
    if (pagination.hasMore && !isLoadingMore && !loading) {
      scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      loadLogs(pagination.nextCursor, true, appliedFilters);
    }
  };

  const handleSort = (column: string) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  const handleColumnToggle = (columnId: string, visible: boolean) => {
    setVisibleColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible } : col
      )
    );
  };

  const handleFiltersApply = (filters: FilterCriteria[]) => {
    setAppliedFilters(filters);
    setPagination(prev => ({ ...prev, nextCursor: null }));
  };

  const handleClearFilters = () => {
    setAppliedFilters([]);
    setPagination(prev => ({ ...prev, nextCursor: null }));
  };

  // Process rows for display - format timestamp only, JSON values will be formatted by column.format in DataTable
  const displayRows = logs.map(log => ({
    ...log,
    timestamp: formatTimestamp(log.timestamp)
  }));

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        flexShrink: 0,
        backgroundColor: 'white',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 0.5
      }}>
        
        {error && (
          <Alert severity={getErrorSeverity(error)} sx={{ m: 2, mt: 0 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CompactFilterPanel
              columns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onFiltersApply={handleFiltersApply}
              onClearFilters={handleClearFilters}
              onAddNew={() => {}}
              filterType="log"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading...
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={() => {
                loadLogs(null, false, appliedFilters);
              }}
              sx={{ minWidth: 'auto', padding: '8px' }}
            >
              <RefreshIcon />
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Table Section */}
      <Box sx={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        mb: 3
      }}>
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          overflow: 'hidden', // Clip content, scrolling handled by TableContainer
        }}>
          <DataTable
            columns={columns}
            rows={displayRows}
            page={0}
            rowsPerPage={displayRows.length}
            totalRows={pagination.total}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            hidePagination={true}
            {...({ onSort: handleSort, sortConfig } as any)}
          />
        </Box>
      </Box>

      {/* Load More Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2,
        mb: 3, 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {logs.length} of {pagination.total}
        </Typography>
        
        {isLoadingMore ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1
          }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : pagination.hasMore ? (
          <button 
            onClick={handleLoadMore}
            style={{
              padding: '6px 16px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: '#1976d2',
              backgroundColor: 'transparent',
              border: '1px solid #1976d2',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Load More
          </button>
        ) : logs.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            No more records
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default LogsView;

